import React from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "@phosphor-icons/react";
import { isFigureToCopy } from "./copyFigure";

/**
 * A2-478 (operator 2026-10-21) — the return is copied by hand onto the
 * authority's own online form, which works some boxes out itself and fills
 * others in. The top of each form says what is paid or reclaimed and in
 * which box, how many boxes to copy and how many to check, and one switch
 * shows only the boxes to copy. Replaces the A2-476 "boxes you fill in"
 * bar: a box the app computes is often one the operator copies.
 */
const fmt = (v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const CopyToFormBar = ({ boxes, liability, readyToFile, onlyCopy, setOnlyCopy, requiredCount = 0,
  entryScale = 1, entryUnit = null }) => {
  const { t } = useTranslation();
  const list = boxes || [];
  const count = (role) => list.filter((b) => b.portal === role).length;
  // operator 2026-10-21: "73 boxes to copy" counted the zeros; count the
  // boxes that carry a figure, and say how many more are 0.
  const copyAll = count("enter");
  const copy = list.filter((b) => isFigureToCopy(b, entryScale)).length;
  const check = count("calculated") + count("prefilled");
  if (!copyAll && !liability) return null;
  const where = (part) => part?.display_ref
    ? t("ui.country_return.copy_bar.in_box", { ref: part.display_ref, defaultValue: `Box ${part.display_ref} on the form` })
    : (part?.label_local || part?.label_english || "");
  // Hungary's form takes thousands of forints: give the figure to type.
  const amount = (part) => (entryScale > 1 && entryUnit
    ? `${(Number(part.amount) / entryScale).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${entryUnit}`
    : fmt(part.amount));
  const HEAD = {
    pay: t("ui.country_return.copy_bar.to_pay", "To pay"),
    reclaim: t("ui.country_return.copy_bar.in_favour", "In your favour (to reclaim or carry forward)"),
    nil: t("ui.country_return.copy_bar.nil", "Nothing to pay or reclaim"),
  };
  // "both" (France): line 32 is due even while line 25 holds a credit.
  const parts = !liability ? []
    : liability.direction === "both" ? [["pay", liability], ["reclaim", liability.reclaim || {}]]
    : [[liability.direction, liability]];
  return (
    <div data-testid="copy-bar" className="mb-4 rounded-lg border-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 px-4 py-3">
      {liability && (
        <div data-testid="copy-bar-liability" data-direction={liability.direction}
             data-boxes={[liability.box_id, liability.reclaim?.box_id].filter(Boolean).join(" ")}
             className="pb-2 mb-2 border-b border-indigo-200 dark:border-indigo-800 space-y-1">
          {parts.map(([dir, part]) => (
            <div key={dir} data-testid={`copy-bar-part-${dir}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-sm font-semibold text-slate-900 dark:text-slate-50">{HEAD[dir]}</span>
              {dir !== "nil" && (
                <span data-testid={`copy-bar-amount-${dir}`} className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-50">
                  {amount(part)}
                </span>
              )}
              <span data-testid={`copy-bar-where-${dir}`} className="text-sm text-slate-700 dark:text-slate-300">{where(part)}</span>
            </div>
          ))}
          {!readyToFile && (
            <div data-testid="copy-bar-not-final" className="text-xs font-medium text-amber-800 dark:text-amber-200">
              {t("ui.country_return.copy_bar.not_final", "Not final until the problems above are fixed")}
            </div>
          )}
        </div>
      )}
      {copyAll > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Copy size={18} weight="bold" className="shrink-0 text-indigo-700 dark:text-indigo-300" aria-hidden="true" />
          <span data-testid="copy-bar-count" className="flex-1 min-w-0 text-sm text-slate-900 dark:text-slate-50 tabular-nums">
            {t("ui.country_return.copy_bar.count", { copy, zero: copyAll - copy, check,
              defaultValue: `${copy} box(es) with a figure to copy onto the form · ${copyAll - copy} more are 0 · ${check} the form works out or fills in — check they match` })}
            {requiredCount > 0 && (
              <strong data-testid="copy-bar-required" className="ml-2 text-amber-800 dark:text-amber-200">
                {t("ui.country_return.copy_bar.required", { count: requiredCount,
                  defaultValue: `· ${requiredCount} to fill in here before filing` })}
              </strong>
            )}
          </span>
          <button
            type="button" data-testid="copy-bar-only" aria-pressed={onlyCopy}
            onClick={() => setOnlyCopy(!onlyCopy)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${onlyCopy
              ? "bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700"
              : "bg-white dark:bg-slate-900 border-indigo-400 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"}`}
          >
            {/* one stable label: aria-pressed carries the on/off state */}
            {t("ui.country_return.copy_bar.only", "Show only the figures to copy")}
          </button>
        </div>
      )}
      {/* A2-537 (Antigravity UX review U14): the filtered view gave no sign that boxes were hidden */}
      {onlyCopy && (
        <div data-testid="copy-bar-filtered-note" className="mt-2 text-xs text-indigo-800 dark:text-indigo-200">
          {t("ui.country_return.copy_bar.filtered", "Other boxes are hidden, not zero. Press the button again to show every box.")}
        </div>
      )}
    </div>
  );
};
